import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "dialog-body payout-success"
}
const _hoisted_2 = { class: "text-blue" }
const _hoisted_3 = { class: "text-body-light" }
const _hoisted_4 = { class: "text-body-light" }
const _hoisted_5 = { class: "amount-requested" }
const _hoisted_6 = {
  key: 1,
  class: "dialog-body"
}
const _hoisted_7 = { class: "text-body-light" }
const _hoisted_8 = { class: "h1 text-blue" }
const _hoisted_9 = {
  key: 0,
  class: "text-body-light"
}
const _hoisted_10 = { class: "text-info" }
const _hoisted_11 = { class: "textfield" }
const _hoisted_12 = {
  for: "PayoutAmount",
  class: "text-body-light"
}
const _hoisted_13 = {
  key: 0,
  class: "prefix"
}
const _hoisted_14 = {
  key: 1,
  class: "prefix"
}
const _hoisted_15 = ["min", "max"]
const _hoisted_16 = {
  key: 0,
  class: "text-info converted-payout-amount"
}
const _hoisted_17 = { class: "text-info" }
const _hoisted_18 = {
  key: 1,
  class: "text-error"
}
const _hoisted_19 = { key: 1 }
const _hoisted_20 = {
  key: 2,
  class: "dialog-actions"
}
const _hoisted_21 = ["disabled"]
const _hoisted_22 = { class: "text-info" }

import { ref, computed } from 'vue'
import { useGettext } from 'vue3-gettext'
import { useStore } from 'vuex'
import Dialog from '@/components/CommonUI/Dialog.vue'
import DropdownUI from '@/components/CommonUI/DropdownUI.vue'
import Icon from '@/components/CommonUI/Icon.vue'
import { AccountTypes, supportedCurrencyConversions } from '@/constants'
import { getErrorMessage } from '@/helpers/errorHandler'
import { formatAmount } from '@/helpers/formatAmount'
import { formatPrice } from '@/helpers/formatPrice'
import { getCurrencySymbol } from '@/helpers/getCurrencySymbol'

const MIN_AMOUNT = 25
const AMOUNT_MINUS_FEE = 0.99


export default /*@__PURE__*/_defineComponent({
  __name: 'PayoutRequestDialog',
  emits: ["payout-request-sent"],
  setup(__props, { expose: __expose }) {



const { $gettext } = useGettext()
const store = useStore()

const dialog = ref<InstanceType<typeof Dialog> | null>(null)

const availableBalance = ref(0)
const error = ref<string | null>(null)
const isAmountInvalid = ref(false)
const isSending = ref(false)
const payoutAmount = ref<string | null>(null)
const payoutTransaction = ref<Transaction | null>(null)
const selectedAccountId = ref(0)

const publicPath = process.env.BASE_URL

const userCurrency = computed(() => store.state.userProfile.currency)
const userCurrencyConversionRate = computed(() => store.state.userCurrencyConversionRate)
const currencySymbol = computed(() => getCurrencySymbol(userCurrency.value || 'GBP'))

const activePayoutMethods = computed<PayoutAccount[]>(() => store.getters.activePayoutMethods)
const isImpersonateMode = computed<boolean>(() => store.getters.isImpersonateMode)
const isUserCurrencyGBP = computed<boolean>(() => store.getters.isUserCurrencyGBP)

const payoutMethodDropdownOptions = computed<DropdownOption[]>(
  () => activePayoutMethods.value.map(({ id, name, type }) => ({
    id: Number(id),
    name,
    iconPath:
      type === AccountTypes.payoneer
        ? String(new URL('../assets/payoneer-icon.svg', import.meta.url))
        : type === AccountTypes.tipaltiPaypal
          ? String(new URL('../assets/paypal-icon.svg', import.meta.url))
          : String(new URL('../assets/bank-building.svg', import.meta.url)),
  })),
)

const reverseConversionRate = computed(() => 1 / userCurrencyConversionRate.value)
const preSelectedPayoutMethod = computed(() => activePayoutMethods.value[0])
const selectedPayoutAccount = computed<PayoutAccount | undefined>(() => activePayoutMethods.value.find(
  account => account.id === selectedAccountId.value,
))

const minAmount = computed<number>(() => Number(selectedPayoutAccount.value?.minimumPayoutAmount) || MIN_AMOUNT)
const maxAmount = computed<number>(() => Number((availableBalance.value * userCurrencyConversionRate.value).toFixed(2)))

const isPayoneerSelected = computed<boolean>(() => selectedPayoutAccount.value?.type === AccountTypes.payoneer)
const isWithdrawalEnabled = computed<boolean>(() => Boolean(
  payoutAmount.value
    && Number(payoutAmount.value) >= minAmount.value
    && Number(payoutAmount.value) <= maxAmount.value,
))

const convertedAmount = computed<number | null>(() => {
  if (isUserCurrencyGBP.value) return null

  const baseAmount = Number(payoutAmount.value || 0)

  if (
    selectedPayoutAccount.value
    && !isPayoneerSelected.value
    && supportedCurrencyConversions.includes(selectedPayoutAccount.value.currency)
  ) {
    return baseAmount * AMOUNT_MINUS_FEE * reverseConversionRate.value
  }

  return baseAmount * reverseConversionRate.value
})

const postPayoutRequest = (payload: {
  payoutAmount: string
  payoutAccountId: number
  requestedAmount: string
  requestedCurrency: string
}): Promise<Transaction> => store.dispatch('postPayoutRequest', payload)

const restrictInput = (evt: KeyboardEvent) => {
  const value = (evt.target as HTMLInputElement).value

  if (!/^\d$/.test(evt.key) && evt.key !== '.') {
    evt.preventDefault()
    return
  }

  if (evt.key === '.' && value.includes('.')) {
    evt.preventDefault()
    return
  }

  if (value.includes('.') && value.split('.')[1].length > 1) {
    evt.preventDefault()
  }
}

const checkInputAmount = (evt: Event) => {
  const value = (evt.target as HTMLInputElement).value

  error.value = null
  isAmountInvalid.value = false

  if (!value) return

  if (Number(value) > maxAmount.value) {
    payoutAmount.value = String(maxAmount.value)
  }

  if (Number(value) < minAmount.value) {
    error.value = $gettext('Your payout must be at least £%{ min }.', { min: String(minAmount.value) })
    isAmountInvalid.value = true
  }
}

const formatInputAmount = () => {
  if (!payoutAmount.value) return
  payoutAmount.value = Number(payoutAmount.value).toFixed(2)
}

const hide = () => {
  dialog.value?.hide()
  error.value = null
  payoutTransaction.value = null
}

const show = (balance: number) => {
  availableBalance.value = balance

  if (preSelectedPayoutMethod.value) {
    selectedAccountId.value = preSelectedPayoutMethod.value.id
  }
  dialog.value?.show()

  if (isImpersonateMode.value) {
    error.value = $gettext('Admins are not allowed to make withdrawal requests on behalf of a user')
  }
}

const sendPayoutRequest = async (): Promise<void> => {
  if (!payoutAmount.value || isImpersonateMode.value) return

  isSending.value = true

  try {
    const payoutAmountInGBP = Number(payoutAmount.value) * reverseConversionRate.value

    payoutTransaction.value = await postPayoutRequest({
      payoutAmount: payoutAmountInGBP.toFixed(2),
      payoutAccountId: selectedAccountId.value,
      requestedAmount: payoutAmount.value,
      requestedCurrency: userCurrency.value,
    })
  } catch (err) {
    error.value = getErrorMessage(err)
  }

  isSending.value = false
}

const selectPayoutAccount = (id: number) => {
  selectedAccountId.value = id
}

__expose({
  show,
  hide,
})

return (_ctx: any,_cache: any) => {
  const _component_lottie_player = _resolveComponent("lottie-player")!
  const _directive_translate = _resolveDirective("translate")!

  return (_openBlock(), _createBlock(Dialog, {
    ref_key: "dialog",
    ref: dialog,
    class: "payout-dialog",
    dismissible: "",
    onDialogClosed: _cache[3] || (_cache[3] = ($event: any) => (payoutTransaction.value ? _ctx.$emit('payout-request-sent') : null))
  }, {
    default: _withCtx(() => [
      (payoutTransaction.value && payoutAmount.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_lottie_player, {
              src: `${_unref(publicPath)}/assets/78667-confetti.json`,
              background: "transparent",
              speed: "1",
              style: {"position":"absolute","top":"-110px","left":"0","z-index":"-1"},
              autoplay: ""
            }, null, 8, ["src"]),
            _createVNode(_component_lottie_player, {
              src: `${_unref(publicPath)}/assets/97240-success.json`,
              background: "transparent",
              speed: "1",
              style: {"width":"180px","height":"180px","margin":"0 auto"},
              autoplay: ""
            }, null, 8, ["src"]),
            _withDirectives((_openBlock(), _createElementBlock("h2", _hoisted_2, [...(_cache[4] || (_cache[4] = [
              _createTextVNode(" Payout Successful ", -1)
            ]))])), [
              [_directive_translate]
            ]),
            _withDirectives((_openBlock(), _createElementBlock("p", _hoisted_3, [...(_cache[5] || (_cache[5] = [
              _createTextVNode(" Your request has been approved and will be paid out shortly. ", -1)
            ]))])), [
              [_directive_translate]
            ]),
            _cache[7] || (_cache[7] = _createElementVNode("hr", null, null, -1)),
            _withDirectives((_openBlock(), _createElementBlock("small", _hoisted_4, [...(_cache[6] || (_cache[6] = [
              _createTextVNode("Amount requested:", -1)
            ]))])), [
              [_directive_translate]
            ]),
            _createElementVNode("p", _hoisted_5, _toDisplayString(isUserCurrencyGBP.value ? _unref(formatAmount)(payoutTransaction.value.amount) : `${currencySymbol.value}${payoutAmount.value}`), 1)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _withDirectives((_openBlock(), _createElementBlock("p", _hoisted_7, [...(_cache[8] || (_cache[8] = [
              _createTextVNode(" Your Available Balance ", -1)
            ]))])), [
              [_directive_translate]
            ]),
            _createElementVNode("h2", _hoisted_8, _toDisplayString(_unref(formatAmount)(availableBalance.value)), 1),
            (!isUserCurrencyGBP.value)
              ? (_openBlock(), _createElementBlock("small", _hoisted_9, [
                  _createTextVNode(_toDisplayString(_unref($gettext)('Amount in GBP:')) + " ", 1),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(formatPrice)(availableBalance.value, 'GBP')), 1)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_11, [
              _withDirectives((_openBlock(), _createElementBlock("label", _hoisted_12, [...(_cache[9] || (_cache[9] = [
                _createTextVNode(" Request a payout ", -1)
              ]))])), [
                [_directive_translate]
              ]),
              (isUserCurrencyGBP.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, " £ "))
                : (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(currencySymbol.value), 1)),
              _withDirectives(_createElementVNode("input", {
                id: "PayoutAmount",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((payoutAmount).value = $event)),
                type: "number",
                class: _normalizeClass({ 'text-error': isAmountInvalid.value }),
                inputmode: "decimal",
                min: minAmount.value,
                max: maxAmount.value,
                placeholder: "0.00",
                step: "0.01",
                onKeypress: _cache[1] || (_cache[1] = ($event: any) => (restrictInput($event))),
                onInput: _cache[2] || (_cache[2] = ($event: any) => (checkInputAmount($event))),
                onChange: formatInputAmount
              }, null, 42, _hoisted_15), [
                [_vModelText, payoutAmount.value]
              ])
            ]),
            _createElementVNode("p", null, [
              (convertedAmount.value)
                ? (_openBlock(), _createElementBlock("small", _hoisted_16, [
                    _createTextVNode(_toDisplayString(_unref($gettext)('Amount in GBP')) + ": ", 1),
                    _createElementVNode("span", _hoisted_17, [
                      _createTextVNode(_toDisplayString(_unref(formatPrice)(convertedAmount.value, 'GBP')) + " (" + _toDisplayString(_unref($gettext)('approx')) + ") ", 1),
                      _cache[10] || (_cache[10] = _createElementVNode("br", null, null, -1))
                    ])
                  ]))
                : _createCommentVNode("", true),
              (error.value)
                ? (_openBlock(), _createElementBlock("small", _hoisted_18, [
                    _createVNode(Icon, {
                      name: "danger",
                      fill: "error"
                    }),
                    _createTextVNode(" " + _toDisplayString(error.value), 1)
                  ]))
                : _createCommentVNode("", true)
            ]),
            (activePayoutMethods.value.length && selectedPayoutAccount.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                  (_openBlock(), _createBlock(DropdownUI, {
                    key: selectedPayoutAccount.value?.id,
                    options: payoutMethodDropdownOptions.value,
                    "selected-option-id": selectedPayoutAccount.value?.id,
                    "toggle-class": "button tertiary",
                    "toggle-icon": "withdraw",
                    onSelected: selectPayoutAccount
                  }, null, 8, ["options", "selected-option-id"]))
                ]))
              : _createCommentVNode("", true)
          ])),
      (!payoutTransaction.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _withDirectives((_openBlock(), _createElementBlock("button", {
              disabled: isImpersonateMode.value || !isWithdrawalEnabled.value || isSending.value,
              onClick: sendPayoutRequest
            }, [...(_cache[11] || (_cache[11] = [
              _createTextVNode(" Withdraw ", -1)
            ]))], 8, _hoisted_21)), [
              [_directive_translate]
            ]),
            _createElementVNode("small", _hoisted_22, _toDisplayString(_unref($gettext)('Your payout must be at least £%{ min }.', { min: String(minAmount.value) })), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 512))
}
}

})